var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GET_USER } from '@/graphql/auth';
import client from '@/graphql/client';
import { unwrap } from '@/graphql/federation/unwrap';
import { carTransferAcceptanceFieldSettings, getLocale } from '@/utils/helpers';
import { getAccessToken, setAccessToken } from '@/utils/localStorage';
const { language } = navigator;
const languageAbbreviation = language === null || language === void 0 ? void 0 : language.slice(0, 2).toUpperCase();
export const LOCALE_DEFAULT = getLocale(languageAbbreviation);
export const initialState = {
    companyId: '',
    pending: true,
    error: null,
    user: {
        id: '',
        firstName: '',
        lastName: '',
        phone: '',
        avatarUrl: '',
        email: '',
        lang: LOCALE_DEFAULT
    },
    autoPark: {
        countryCode: LOCALE_DEFAULT
    },
    settings: {
        companyName: '',
        companyLogoUrl: null,
        officeTripsEnabled: false,
        financeDetails: false,
        chatwootEnabled: false,
        officeTripsDriverSelfBooking: false,
        balanceDetails: false,
        debtDetails: false,
        depositDetails: false,
        tollRoadsAccounting: null,
        carTransferAcceptance: {
            fieldSettings: carTransferAcceptanceFieldSettings
        }
    },
    chatwoot: null
};
function createExtraActions() {
    function authLogoutRequest() {
        return createAsyncThunk('auth/authLogoutRequest', (args_1, _a) => __awaiter(this, [args_1, _a], void 0, function* (args, { dispatch }) {
            var _b, _c, _d;
            try {
                dispatch(authSlice.actions.authSetPending(true));
                setAccessToken(null);
                yield client.resetStore();
                dispatch(authSlice.actions.authLogout());
                window.location.assign('/');
            }
            catch (error) {
                const errorCode = (_d = (_c = (_b = error === null || error === void 0 ? void 0 : error.graphQLErrors) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.extensions) === null || _d === void 0 ? void 0 : _d.code;
                dispatch(authSlice.actions.authSetError(errorCode));
            }
            finally {
                window.location.assign('/');
            }
        }));
    }
    function checkAuth() {
        return createAsyncThunk('auth/checkAuth', (args_1, _a) => __awaiter(this, [args_1, _a], void 0, function* (args, { dispatch, getState }) {
            var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1;
            const token = getAccessToken();
            if (!token) {
                dispatch(AuthActionCreators.authSetPending(false));
                return;
            }
            try {
                const state = getState();
                let { companyId, user, settings, autoPark } = state === null || state === void 0 ? void 0 : state.auth;
                const { data } = yield client.query({ query: GET_USER, fetchPolicy: 'network-only' });
                const meResult = unwrap(data, 'driverBackOffice');
                if (!(meResult === null || meResult === void 0 ? void 0 : meResult.me))
                    throw new Error('Failed to fetch user data');
                const me = meResult.me;
                companyId = (_c = (_b = me === null || me === void 0 ? void 0 : me.user) === null || _b === void 0 ? void 0 : _b.companyId) !== null && _c !== void 0 ? _c : '';
                user = {
                    id: (_d = me === null || me === void 0 ? void 0 : me.user) === null || _d === void 0 ? void 0 : _d.id,
                    phone: (_e = me === null || me === void 0 ? void 0 : me.user) === null || _e === void 0 ? void 0 : _e.phone,
                    firstName: (_f = me === null || me === void 0 ? void 0 : me.user) === null || _f === void 0 ? void 0 : _f.firstName,
                    lastName: (_g = me === null || me === void 0 ? void 0 : me.user) === null || _g === void 0 ? void 0 : _g.lastName,
                    lang: (_h = me === null || me === void 0 ? void 0 : me.user) === null || _h === void 0 ? void 0 : _h.lang,
                    avatarUrl: (_j = me === null || me === void 0 ? void 0 : me.user) === null || _j === void 0 ? void 0 : _j.avatarUrl,
                    email: (_k = me === null || me === void 0 ? void 0 : me.user) === null || _k === void 0 ? void 0 : _k.email
                };
                settings = {
                    companyName: (_l = me === null || me === void 0 ? void 0 : me.settings) === null || _l === void 0 ? void 0 : _l.companyName,
                    companyLogoUrl: (_m = me === null || me === void 0 ? void 0 : me.settings) === null || _m === void 0 ? void 0 : _m.companyLogoUrl,
                    officeTripsEnabled: (_o = me === null || me === void 0 ? void 0 : me.settings) === null || _o === void 0 ? void 0 : _o.officeTripsEnabled,
                    financeDetails: (_p = me === null || me === void 0 ? void 0 : me.settings) === null || _p === void 0 ? void 0 : _p.financeDetails,
                    chatwootEnabled: (_q = me === null || me === void 0 ? void 0 : me.settings) === null || _q === void 0 ? void 0 : _q.chatwootEnabled,
                    officeTripsDriverSelfBooking: (_r = me === null || me === void 0 ? void 0 : me.settings) === null || _r === void 0 ? void 0 : _r.officeTripsDriverSelfBooking,
                    balanceDetails: (_s = me === null || me === void 0 ? void 0 : me.settings) === null || _s === void 0 ? void 0 : _s.balanceDetails,
                    debtDetails: (_t = me === null || me === void 0 ? void 0 : me.settings) === null || _t === void 0 ? void 0 : _t.debtDetails,
                    depositDetails: (_u = me === null || me === void 0 ? void 0 : me.settings) === null || _u === void 0 ? void 0 : _u.depositDetails,
                    tollRoadsAccounting: (_v = me === null || me === void 0 ? void 0 : me.settings) === null || _v === void 0 ? void 0 : _v.tollRoadsAccounting,
                    carTransferAcceptance: {
                        fieldSettings: (_x = (_w = me === null || me === void 0 ? void 0 : me.settings) === null || _w === void 0 ? void 0 : _w.carTransferAcceptance) === null || _x === void 0 ? void 0 : _x.fieldSettings
                    }
                };
                autoPark = {
                    countryCode: (_y = me === null || me === void 0 ? void 0 : me.autoPark) === null || _y === void 0 ? void 0 : _y.countryCode
                };
                dispatch(AuthActionCreators.authSetCompanyId(companyId));
                dispatch(AuthActionCreators.authLoginSuccess(user));
                dispatch(AuthActionCreators.authSetSettings(settings));
                dispatch(AuthActionCreators.authSetAutoPark(autoPark));
            }
            catch (error) {
                const errorCode = (_1 = (_0 = (_z = error === null || error === void 0 ? void 0 : error.graphQLErrors) === null || _z === void 0 ? void 0 : _z[0]) === null || _0 === void 0 ? void 0 : _0.extensions) === null || _1 === void 0 ? void 0 : _1.code;
                if (errorCode === 'UNAUTHORIZED') {
                    setAccessToken(null);
                }
                if (errorCode === 'BLOCKED') {
                    setAccessToken(null);
                    dispatch(authSlice.actions.authSetError('BLOCKED_AUTHORIZED'));
                }
            }
            finally {
                dispatch(AuthActionCreators.authSetPending(false));
            }
        }));
    }
    function authGetUser() {
        return createAsyncThunk('auth/authGetUser', (args_1, _a) => __awaiter(this, [args_1, _a], void 0, function* (args, { dispatch }) {
            var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
            try {
                const { data } = yield client.query({ query: GET_USER, fetchPolicy: 'network-only' });
                const meResult = unwrap(data, 'driverBackOffice');
                if (!(meResult === null || meResult === void 0 ? void 0 : meResult.me))
                    throw new Error('Failed to fetch user data');
                const me = meResult.me;
                const { companyId, id, phone, firstName, lastName, lang, avatarUrl, email } = me === null || me === void 0 ? void 0 : me.user;
                const user = {
                    id, phone, firstName, lastName, lang, avatarUrl, email
                };
                const settings = {
                    companyName: (_b = me === null || me === void 0 ? void 0 : me.settings) === null || _b === void 0 ? void 0 : _b.companyName,
                    companyLogoUrl: (_c = me === null || me === void 0 ? void 0 : me.settings) === null || _c === void 0 ? void 0 : _c.companyLogoUrl,
                    officeTripsEnabled: (_d = me === null || me === void 0 ? void 0 : me.settings) === null || _d === void 0 ? void 0 : _d.officeTripsEnabled,
                    financeDetails: (_e = me === null || me === void 0 ? void 0 : me.settings) === null || _e === void 0 ? void 0 : _e.financeDetails,
                    chatwootEnabled: (_f = me === null || me === void 0 ? void 0 : me.settings) === null || _f === void 0 ? void 0 : _f.chatwootEnabled,
                    officeTripsDriverSelfBooking: (_g = me === null || me === void 0 ? void 0 : me.settings) === null || _g === void 0 ? void 0 : _g.officeTripsDriverSelfBooking,
                    balanceDetails: (_h = me === null || me === void 0 ? void 0 : me.settings) === null || _h === void 0 ? void 0 : _h.balanceDetails,
                    debtDetails: (_j = me === null || me === void 0 ? void 0 : me.settings) === null || _j === void 0 ? void 0 : _j.debtDetails,
                    depositDetails: (_k = me === null || me === void 0 ? void 0 : me.settings) === null || _k === void 0 ? void 0 : _k.depositDetails,
                    tollRoadsAccounting: (_l = me === null || me === void 0 ? void 0 : me.settings) === null || _l === void 0 ? void 0 : _l.tollRoadsAccounting,
                    carTransferAcceptance: {
                        fieldSettings: (_o = (_m = me === null || me === void 0 ? void 0 : me.settings) === null || _m === void 0 ? void 0 : _m.carTransferAcceptance) === null || _o === void 0 ? void 0 : _o.fieldSettings
                    }
                };
                const autoPark = {
                    countryCode: (_p = me === null || me === void 0 ? void 0 : me.autoPark) === null || _p === void 0 ? void 0 : _p.countryCode
                };
                dispatch(authSlice.actions.authSetCompanyId(companyId !== null && companyId !== void 0 ? companyId : ''));
                dispatch(authSlice.actions.authLoginSuccess(user));
                dispatch(AuthActionCreators.authSetSettings(settings));
                dispatch(AuthActionCreators.authSetAutoPark(autoPark));
                dispatch(authSlice.actions.authSetError(''));
            }
            catch (error) {
                const errorCode = (_s = (_r = (_q = error === null || error === void 0 ? void 0 : error.graphQLErrors) === null || _q === void 0 ? void 0 : _q[0]) === null || _r === void 0 ? void 0 : _r.extensions) === null || _s === void 0 ? void 0 : _s.code;
                dispatch(authSlice.actions.authSetError(errorCode));
                if (errorCode === 'BLOCKED') {
                    setAccessToken(null);
                }
            }
            finally {
                dispatch(authSlice.actions.authSetPending(false));
            }
        }));
    }
    return {
        authGetUser: authGetUser(),
        checkAuth: checkAuth(),
        authLogoutRequest: authLogoutRequest()
    };
}
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        authSetPending: (state, action) => {
            state.pending = action.payload;
        },
        authSetError: (state, action) => {
            state.error = action.payload;
        },
        authSetCompanyId: (state, action) => {
            state.companyId = action.payload;
        },
        authLoginSuccess: (state, action) => {
            state.user = action.payload;
        },
        authSetSettings: (state, action) => {
            state.settings = action.payload;
        },
        authSetAutoPark: (state, action) => {
            state.autoPark = action.payload;
        },
        changeLang: (state, action) => {
            state.user.lang = action.payload;
        },
        setChatwoot: (state, action) => {
            state.chatwoot = action.payload;
        },
        authLogout: () => initialState
    }
});
const { authLogout, authSetPending, authSetCompanyId, authLoginSuccess, authSetSettings, authSetAutoPark, changeLang, setChatwoot } = authSlice.actions;
const extraActions = createExtraActions();
export const AuthActionCreators = Object.assign({ authLogout,
    authSetPending,
    authSetCompanyId,
    authLoginSuccess,
    authSetSettings,
    authSetAutoPark,
    changeLang,
    setChatwoot }, extraActions);
export default authSlice.reducer;
